import { useCallback, useMemo, useState } from 'react';
import { useAuthSession } from '@/api';
import {
  betslipApplyReward,
  betslipClearReward,
  getBetslipTicket,
} from '@/betslip/betslip-store';
import { useBetslipTicket } from '@/betslip/useBetslip';
import { hasKreditBetCreditor } from '@/domain/kredit-bet';
import { formatAmount } from '@/domain/player-balance';
import type { PlaceBetMessage } from '@/domain/place-bet';
import {
  activePlayerRewards,
  appliedRewardFromTicket,
  checkRewardQuota,
  rewardDisplayName,
  type PlayerReward,
} from '@/domain/rewards';

export interface UseBetslipRewardsResult {
  showBanner: boolean;
  mode: 'idle' | 'applied';
  availableCount: number;
  activeRewards: PlayerReward[];
  appliedReward: PlayerReward | null;
  appliedLabel: string;
  stakeLocked: boolean;
  sheetOpen: boolean;
  openSheet: () => void;
  closeSheet: () => void;
  applyFromSheet: (reward: PlayerReward | null) => void;
  errorMessage: PlaceBetMessage | null;
  clearError: () => void;
  blockedByKreditBet: boolean;
}

/**
 * FreeBet / BackupBet apply flow for the betslip footer.
 */
export function useBetslipRewards(): UseBetslipRewardsResult {
  const { loggedIn, user } = useAuthSession();
  const ticket = useBetslipTicket();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<PlaceBetMessage | null>(null);

  const currency = user?.CurrencyCode?.trim() || user?.CurrCode?.trim() || '';
  const sessionRewards = useMemo(
    () => activePlayerRewards(user?.Rewards),
    [user?.Rewards],
  );
  const appliedReward = appliedRewardFromTicket(ticket);
  const activeRewards = useMemo(() => {
    if (!appliedReward) return sessionRewards;
    if (sessionRewards.some((r) => r.Id === appliedReward.Id)) return sessionRewards;
    return [appliedReward, ...sessionRewards];
  }, [appliedReward, sessionRewards]);
  const availableCount = sessionRewards.length;
  const blockedByKreditBet = hasKreditBetCreditor(ticket);

  const showBanner =
    loggedIn &&
    ticket.Items.length > 0 &&
    (availableCount > 0 || appliedReward != null);

  const mode: 'idle' | 'applied' = appliedReward ? 'applied' : 'idle';

  const appliedLabel = appliedReward
    ? rewardDisplayName(appliedReward, formatAmount, currency)
    : '';

  const openSheet = useCallback(() => {
    setErrorMessage(null);
    if (blockedByKreditBet) {
      setErrorMessage({
        key: 'CannotUserRewardBonusWithCreditBet',
        defaultValue: 'Cannot use a reward bonus with Kredit Bet',
      });
      return;
    }
    if (activeRewards.length === 0 && !appliedReward) return;
    setSheetOpen(true);
  }, [blockedByKreditBet, activeRewards.length, appliedReward]);

  const closeSheet = useCallback(() => setSheetOpen(false), []);

  const applyFromSheet = useCallback(
    (reward: PlayerReward | null) => {
      setErrorMessage(null);
      if (reward == null) {
        betslipClearReward();
        setSheetOpen(false);
        return;
      }

      if (blockedByKreditBet) {
        setErrorMessage({
          key: 'CannotUserRewardBonusWithCreditBet',
          defaultValue: 'Cannot use a reward bonus with Kredit Bet',
        });
        setSheetOpen(false);
        return;
      }

      const current = getBetslipTicket();
      const quota = checkRewardQuota(reward, current.Odds);
      if (!quota.ok) {
        setErrorMessage(quota.message);
        setSheetOpen(false);
        return;
      }

      betslipApplyReward(reward);
      setSheetOpen(false);
    },
    [blockedByKreditBet],
  );

  return {
    showBanner,
    mode,
    availableCount,
    activeRewards,
    appliedReward,
    appliedLabel,
    stakeLocked: appliedReward != null,
    sheetOpen,
    openSheet,
    closeSheet,
    applyFromSheet,
    errorMessage,
    clearError: () => setErrorMessage(null),
    blockedByKreditBet,
  };
}
